import { useEffect, useState } from 'react';
import { Box, Button, Container, Link as MuiLink, Stack, Typography, alpha, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';

import {
    COOKIE_CONSENT_ACCEPT,
    COOKIE_CONSENT_DECLINE,
    COOKIE_CONSENT_LEARN_MORE,
    COOKIE_CONSENT_MESSAGE,
} from '../../locales/types';

const STORAGE_KEY = 'lpc-cookie-consent-v1';

/**
 * Lightweight bottom-sheet cookie consent banner.
 * Stores a single string ("accepted" | "declined") in localStorage so it only
 * appears once per visitor until they clear storage.
 */
const CookieConsentBanner = () => {
    const theme = useTheme();
    const { t } = useTranslation();
    const [visible, setVisible] = useState(false);

    useEffect(() => {
        try {
            const decision = window.localStorage.getItem(STORAGE_KEY);
            if (!decision) setVisible(true);
        } catch {
            // localStorage blocked — render anyway
            setVisible(true);
        }
    }, []);

    const decide = (value) => {
        try {
            window.localStorage.setItem(STORAGE_KEY, value);
        } catch {
            // ignore
        }
        // Notify any listeners (e.g. GA4 Consent Mode in index.html) of the change.
        try {
            window.dispatchEvent(new CustomEvent('lpc-consent-change', { detail: value }));
        } catch {
            // CustomEvent unsupported (very old browsers) — ignore.
        }
        setVisible(false);
    };

    if (!visible) return null;

    return (
        <Box
            role="dialog"
            aria-live="polite"
            aria-label="Cookie consent"
            sx={{
                position: 'fixed',
                left: { xs: 8, md: 16 },
                right: { xs: 8, md: 16 },
                // Sit above the mobile sticky bar (xs/sm) so they don't overlap
                bottom: { xs: 'calc(env(safe-area-inset-bottom) + 60px)', md: 16 },
                zIndex: (th) => th.zIndex.snackbar + 1,
                bgcolor: 'background.paper',
                color: 'text.primary',
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                boxShadow: '0 10px 30px rgba(0,0,0,0.18)',
                p: { xs: 2, md: 2.5 },
                maxWidth: 760,
                mx: 'auto',
            }}
        >
            <Container disableGutters maxWidth="md">
                <Stack
                    direction={{ xs: 'column', md: 'row' }}
                    spacing={2}
                    alignItems={{ xs: 'stretch', md: 'center' }}
                    justifyContent="space-between"
                >
                    <Typography sx={{ fontSize: { xs: '0.85rem', md: '0.9rem' }, lineHeight: 1.6, flex: 1 }}>
                        {t(`component.cookieConsent.${COOKIE_CONSENT_MESSAGE}`)}{' '}
                        <MuiLink href="/legal/cookiepolicy.html" target="_blank" rel="noopener" sx={{ fontWeight: 600, color: theme.palette.primary.main }}>
                            {t(`component.cookieConsent.${COOKIE_CONSENT_LEARN_MORE}`)}
                        </MuiLink>
                    </Typography>
                    <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }}>
                        <Button
                            onClick={() => decide('declined')}
                            variant="text"
                            sx={{ color: 'text.secondary', fontWeight: 600 }}
                        >
                            {t(`component.cookieConsent.${COOKIE_CONSENT_DECLINE}`)}
                        </Button>
                        <Button
                            onClick={() => decide('accepted')}
                            variant="contained"
                            sx={{
                                fontWeight: 700,
                                bgcolor: theme.palette.primary.main,
                                '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.85) },
                            }}
                        >
                            {t(`component.cookieConsent.${COOKIE_CONSENT_ACCEPT}`)}
                        </Button>
                    </Stack>
                </Stack>
            </Container>
        </Box>
    );
};

export default CookieConsentBanner;
